import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo } from '@/mocks/about';

export default function AboutPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'success'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('success');
    form.reset();
  };

  return (
    <>
      <Navbar />
      <main>
        <section className="relative w-full h-[280px] md:h-[380px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Chinese%20creative%20office%20panoramic%20view%2C%20modern%20manga%20studio%20interior%20with%20comic%20art%20displayed%20on%20walls%2C%20warm%20ambient%20lighting%2C%20bookshelves%20full%20of%20manga%20volumes%2C%20cozy%20and%20inspiring%20atmosphere%2C%20professional%20interior%20photography&width=1600&height=600&seq=about-hero-bg-01&orientation=landscape"
            alt="布丁漫画网团队"
            title="关于布丁漫画网"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/50"></div>
          <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 pb-10 md:pb-14">
            <div className="max-w-[1280px] mx-auto">
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-2">{t('about:hero_title')}</h1>
              <p className="text-sm md:text-base text-background-50/75 max-w-xl">{t('about:hero_desc')}</p>
            </div>
          </div>
        </section>

        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: t('about:breadcrumb_home'), path: '/' },
              { label: t('about:breadcrumb_about') },
            ]}
          />
        </div>

        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
            <div className="lg:w-1/2">
              <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                {t('about:section_brand_tag')}
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">{t('about:section_brand_title')}</h2>
              <div className="text-sm md:text-base text-foreground-600 leading-relaxed space-y-4">
                {aboutInfo.brandStory.split('\n\n').map((para, idx) => (
                  <p key={idx}>{para}</p>
                ))}
              </div>
            </div>
            <div className="lg:w-1/2 w-full">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src={aboutInfo.teamImage}
                  alt="布丁漫画网团队"
                  title="布丁漫画网 - 我们的团队"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100/60 py-14 md:py-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                {t('about:section_values_tag')}
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{t('about:section_values_title')}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {aboutInfo.values.map((val) => (
                <div key={val.title} className="bg-background-50 rounded-2xl p-6 md:p-7 text-center border border-background-200/70">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-secondary-100 flex items-center justify-center">
                    <i className={`${val.icon} text-2xl text-secondary-600 w-7 h-7 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{val.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{val.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
              {t('about:section_milestones_tag')}
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{t('about:section_milestones_title')}</h2>
          </div>
          <div className="relative max-w-3xl mx-auto">
            <div className="absolute left-[27px] md:left-1/2 top-0 bottom-0 w-px bg-background-200 -translate-x-1/2 hidden md:block"></div>
            <div className="absolute left-[27px] top-0 bottom-0 w-px bg-background-200 md:hidden"></div>
            <div className="space-y-8">
              {aboutInfo.milestones.map((m, idx) => (
                <div key={m.year} className={`relative flex items-start gap-6 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className="absolute md:static left-0 w-[54px] md:w-auto flex items-center justify-center flex-shrink-0">
                    <div className="w-[54px] h-[54px] rounded-full bg-primary-500 text-background-50 flex items-center justify-center font-bold text-sm z-10">
                      {m.year}
                    </div>
                  </div>
                  <div className="ml-[70px] md:ml-0 md:w-1/2 md:px-8 bg-background-50 rounded-2xl p-5 border border-background-200/70">
                    <p className="text-sm text-foreground-700 leading-relaxed">{m.event}</p>
                  </div>
                  {idx % 2 === 0 ? <div className="hidden md:block md:w-1/2"></div> : <div className="hidden md:block md:w-1/2"></div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-foreground-950 py-14 md:py-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-flex items-center px-4 py-1.5 rounded-full border border-background-50/25 text-background-50 text-xs font-medium mb-4">
                {t('about:section_platform_tag')}
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50">{t('about:section_platform_title')}</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {aboutInfo.platformData.map((item) => (
                <div key={item.label} className="text-center p-6 md:p-8 rounded-2xl border border-foreground-800/60">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-secondary-500/20 flex items-center justify-center">
                    <i className={`${item.icon} text-xl text-secondary-400 w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <p className="font-heading text-3xl md:text-4xl font-bold text-background-50 mb-1">{item.value}</p>
                  <p className="text-xs md:text-sm text-foreground-400">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-14 md:py-20">
          <div className="text-center mb-10 md:mb-14">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
              {t('about:section_business_tag')}
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{t('about:section_business_title')}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
            {aboutInfo.businessCooperation.map((item) => (
              <div key={item.title} className="bg-background-100/60 rounded-2xl p-6 text-center border border-background-200/70">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-accent-100 flex items-center justify-center">
                  <i className={`${item.icon} text-xl text-accent-600 w-5 h-5 flex items-center justify-center`}></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">{item.title}</h3>
                <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
              <h3 className="text-lg font-semibold text-foreground-950 mb-6">{t('about:form_title')}</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="antispam-field"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-xs font-medium text-foreground-600 mb-1.5">{t('about:form_name')}</label>
                    <input id="name" name="name" type="text" required className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300 transition-colors" placeholder={t('about:form_name')} />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-xs font-medium text-foreground-600 mb-1.5">{t('about:form_company')}</label>
                    <input id="company" name="company" type="text" className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300 transition-colors" placeholder={t('about:form_company')} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-xs font-medium text-foreground-600 mb-1.5">{t('about:form_email')}</label>
                    <input id="email" name="email" type="email" required className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300 transition-colors" placeholder={t('about:form_email')} />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs font-medium text-foreground-600 mb-1.5">{t('about:form_phone')}</label>
                    <input id="phone" name="phone" type="tel" className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300 transition-colors" placeholder={t('about:form_phone')} />
                  </div>
                </div>
                <div>
                  <label htmlFor="cooperation_type" className="block text-xs font-medium text-foreground-600 mb-1.5">{t('about:form_type')}</label>
                  <select id="cooperation_type" name="cooperation_type" required className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-950 focus:outline-none focus:border-accent-300 transition-colors cursor-pointer">
                    <option value="">{t('about:form_type')}</option>
                    <option value="内容合作">{t('about:form_type_content')}</option>
                    <option value="广告投放">{t('about:form_type_ad')}</option>
                    <option value="IP授权">{t('about:form_type_ip')}</option>
                    <option value="媒体合作">{t('about:form_type_media')}</option>
                    <option value="其他">{t('about:form_type_other')}</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-xs font-medium text-foreground-600 mb-1.5">{t('about:form_message')}</label>
                  <textarea id="message" name="message" rows={4} maxLength={500} required className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-300 transition-colors resize-none" placeholder={t('about:form_message')}></textarea>
                  <p className="text-[10px] text-foreground-400 mt-1 text-right">0/500</p>
                </div>
                <button
                  type="submit"
                  className="w-full py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {t('about:form_submit')}
                </button>
                {formStatus === 'success' && (
                  <p className="text-sm text-accent-600 text-center">{t('about:form_success')}</p>
                )}
              </form>
            </div>
          </div>
        </section>

        <section className="bg-background-100/60 py-14 md:py-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                {t('about:section_contact_tag')}
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{t('about:section_contact_title')}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5 max-w-4xl mx-auto">
              <div className="bg-background-50 rounded-2xl p-5 text-center border border-background-200/70 lg:col-span-1">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-map-pin-line text-lg text-secondary-600 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <p className="text-[11px] text-foreground-500 mb-1">{t('about:contact_address')}</p>
                <p className="text-xs text-foreground-800 leading-relaxed">{aboutInfo.contactInfo.address}</p>
              </div>
              <div className="bg-background-50 rounded-2xl p-5 text-center border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-phone-line text-lg text-secondary-600 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <p className="text-[11px] text-foreground-500 mb-1">{t('about:contact_phone')}</p>
                <p className="text-xs text-foreground-800">{aboutInfo.contactInfo.phone}</p>
              </div>
              <div className="bg-background-50 rounded-2xl p-5 text-center border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-mail-line text-lg text-secondary-600 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <p className="text-[11px] text-foreground-500 mb-1">{t('about:contact_email')}</p>
                <p className="text-xs text-foreground-800 break-all">{aboutInfo.contactInfo.email}</p>
              </div>
              <div className="bg-background-50 rounded-2xl p-5 text-center border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-briefcase-line text-lg text-secondary-600 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <p className="text-[11px] text-foreground-500 mb-1">{t('about:contact_business_email')}</p>
                <p className="text-xs text-foreground-800 break-all">{aboutInfo.contactInfo.businessEmail}</p>
              </div>
              <div className="bg-background-50 rounded-2xl p-5 text-center border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-time-line text-lg text-secondary-600 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <p className="text-[11px] text-foreground-500 mb-1">{t('about:contact_hours')}</p>
                <p className="text-xs text-foreground-800">{aboutInfo.contactInfo.workHours}</p>
              </div>
            </div>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'AboutPage',
              name: '关于布丁漫画网',
              description: '布丁漫画网是国内领先的正版漫画在线阅读平台，成立于2018年，拥有2000万+注册用户和3000+部漫画作品。',
              url: 'http://591chaxin.cn/about',
              breadcrumb: {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: '首页', item: 'http://591chaxin.cn/' },
                  { '@type': 'ListItem', position: 2, name: '关于我们' },
                ],
              },
            }),
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: [
                {
                  '@type': 'Question',
                  name: '布丁漫画网的作品是否为正版？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '是的，布丁漫画网所有漫画作品均获得作者或版权方正版授权，我们坚持保障创作者权益，推动漫画产业健康发展。',
                  },
                },
                {
                  '@type': 'Question',
                  name: '布丁漫画网的阅读方式是怎样的？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '布丁漫画网采用先进的下拉式阅读技术，支持流畅的全章节阅读体验，无需翻页等待。同时我们提供无删减的完整内容，让读者看到作者最真实的创作。',
                  },
                },
                {
                  '@type': 'Question',
                  name: '如何与布丁漫画网进行商务合作？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '您可以通过我们官网关于我们页面的合作咨询表单提交合作意向，hello@591chaxin.cn，我们会在1-3个工作日内与您联系。合作类型包括内容合作、广告投放、IP授权和媒体合作。',
                  },
                },
                {
                  '@type': 'Question',
                  name: '布丁漫画网的客服联系方式是什么？',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: '您可以通过客服电话400-888-6666联系我们，hello@591chaxin.cn。我们的工作时间是周一至周五9:00-18:00。',
                  },
                },
              ],
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}
